import { useCallback } from 'react';

import { useEditCanvasStore } from '../../store';
import { type NodeZIndex, sendToFront } from '../../utils/nodeLayering';
import { commands } from '../commands/commands';
import { useCommandHistory } from '../commands/hooks/useCommandHistory';
import { useSelectedCanvasNodes } from '../useSelectedCanvasNodes';

export function useGroupActions() {
  const selectedNodes = useSelectedCanvasNodes();
  const allNodes = useEditCanvasStore((s) => s.nodes);
  const { push } = useCommandHistory();

  const handleGroup = useCallback(async () => {
    const groupMutations = useEditCanvasStore.getState().groupMutations;

    if (!groupMutations) {
      return;
    }

    const nodeIds = selectedNodes.map((n) => n.id);

    const storeNodes = useEditCanvasStore.getState().nodes;
    const remoteNodeIds = nodeIds
      .map((id) => storeNodes.find((n) => n.id === id)?.remoteId)
      .filter((id): id is string => !!id);

    if (remoteNodeIds.length < 2) {
      return;
    }

    const allNodeZIndexes: NodeZIndex[] = allNodes.map((n) => ({ id: n.id, zIndex: n.zIndex ?? 0 }));
    const previousZIndexes = [...allNodeZIndexes];
    const newZIndexes = sendToFront(allNodeZIndexes, new Set(nodeIds));
    const zIndexUpdates = newZIndexes.length > 0 ? { next: newZIndexes, previous: previousZIndexes } : undefined;

    try {
      // API-first: no local state changes until server confirms
      const response = await groupMutations.createGroup({ nodeIds: remoteNodeIds });
      const serverGroupId = response.groupNode.id;

      useEditCanvasStore.getState().setGroupId(nodeIds, serverGroupId);

      if (zIndexUpdates) {
        useEditCanvasStore.getState().updateZIndexes(zIndexUpdates.next);
      }

      // State already applied above — push, not execute, to avoid re-applying
      push(commands.groupNodes(nodeIds, serverGroupId, remoteNodeIds, groupMutations, zIndexUpdates));
    } catch {
      // API failed — no local state was changed, nothing to revert
    }
  }, [selectedNodes, allNodes, push]);

  const handleUngroup = useCallback(async () => {
    const groupMutations = useEditCanvasStore.getState().groupMutations;

    if (!groupMutations) {
      return;
    }

    const nodeIds = selectedNodes.map((n) => n.id);

    const groupId = selectedNodes[0]?.groupId;

    if (!groupId || selectedNodes.some((n) => n.groupId !== groupId)) {
      return;
    }

    const storeNodes = useEditCanvasStore.getState().nodes;
    const remoteNodeIds = nodeIds
      .map((id) => storeNodes.find((n) => n.id === id)?.remoteId)
      .filter((id): id is string => !!id);

    // Optimistic: apply local state immediately for instant feedback
    useEditCanvasStore.getState().setGroupId(nodeIds, undefined);

    try {
      await groupMutations.deleteGroup({ groupNodeId: groupId });

      // State already applied above — push, not execute, to avoid re-applying
      push(commands.ungroupNodes(nodeIds, groupId, remoteNodeIds, groupMutations));
    } catch {
      // Revert on failure
      useEditCanvasStore.getState().setGroupId(nodeIds, groupId);
    }
  }, [selectedNodes, push]);

  return { handleGroup, handleUngroup };
}
